/**
 * Converting a browser selection into character offsets.
 *
 * This is the single most load-bearing primitive in the app: every tag is stored as
 * `[start_char, end_char)` into the working caption, and the server rejects a span
 * whose text does not match the caption at those offsets.
 */

/**
 * DOM Range -> character offsets within `container`'s text content.
 *
 * Ported verbatim from the legacy implementation, which is known to produce offsets
 * that validate against the stored captions for all 77 existing spans.
 *
 * NOTE: `range.toString()` returns TEXT-NODE content, not rendered layout. The
 * container must therefore be a single `white-space: pre-wrap` element containing only
 * text and `<mark>` elements. Splitting paragraphs into `<p>` tags would silently shift
 * every offset. See CaptionSurface.
 */
export function rangeToOffsets(container, range) {
  if (
    !container ||
    !range ||
    !container.contains(range.startContainer) ||
    !container.contains(range.endContainer)
  ) {
    return null;
  }
  const probe = range.cloneRange();
  probe.selectNodeContents(container);
  probe.setEnd(range.startContainer, range.startOffset);
  const start = probe.toString().length;
  return { start_char: start, end_char: start + range.toString().length };
}

/** Place the caret/selection back over a span, so `j`/`k` navigation can scroll to it. */
export function offsetsToRange(container, { start_char, end_char }) {
  if (!container) return null;
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  const range = document.createRange();
  let consumed = 0;
  let startSet = false;

  let node = walker.nextNode();
  while (node) {
    const length = node.textContent.length;
    if (!startSet && consumed + length >= start_char) {
      range.setStart(node, start_char - consumed);
      startSet = true;
    }
    if (startSet && consumed + length >= end_char) {
      range.setEnd(node, end_char - consumed);
      return range;
    }
    consumed += length;
    node = walker.nextNode();
  }
  return startSet ? range : null;
}

/**
 * Read the live selection as offsets, or null when nothing useful is selected.
 *
 * EXACTLY what the annotator selected - no word snapping, no whitespace trimming.
 * Both are tempting tidy-ups and both are wrong for this corpus:
 *
 *   - a single letter is a legitimate span. The plural `s` in "towels" is a factual
 *     claim about how many things there are, and removing it is a real annotation in
 *     the existing data.
 *   - leading and trailing spaces are deliberate. The wrong-uncertainty spans read
 *     `" or pan"` and `"bread or "` precisely so that deleting them leaves clean
 *     prose behind.
 *
 * Word selection is still one double-click away - the browser does it natively.
 */
export function readSelection(container) {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return null;

  const range = selection.getRangeAt(0);
  const offsets = rangeToOffsets(container, range);
  if (!offsets || offsets.end_char <= offsets.start_char) return null;

  const text = container.textContent || '';
  const picked = text.slice(offsets.start_char, offsets.end_char);
  // A selection of pure whitespace is almost certainly a stray drag.
  if (!picked.trim()) return null;

  return {
    ...offsets,
    text: picked,
    // Only used to position the editor popover; absent in non-visual environments.
    rect: range.getBoundingClientRect?.() ?? null,
  };
}
